import { computed, ComputedRef, Ref } from 'vue'

import { TeamSide } from '@collector/shared-domain'
import { getParticipantIdTeamSide } from '@collector/shared-ui-domain'
import { Incident, Participant } from '@collector/sportsapi-client'

import { useEventSettings } from './useEventSettings'
import { useParticipants } from './useParticipants'

export type UseTeamSides = {
  changeSides(): void

  /** home/away for team on left, reacts to changeSides */
  currentFirstTeamSide: ComputedRef<TeamSide>

  /** home/away for team on right, reacts to changeSides */
  currentSecondTeamSide: ComputedRef<TeamSide>

  getWithCurrentTeamSides<T>(
    homeValue: Ref<T>,
    awayValue: Ref<T>,
  ): [ComputedRef<T>, ComputedRef<T>]

  getParticipantsWithCurrentTeamSides(): {
    currentFirstParticipant: ComputedRef<Participant | undefined>
    currentSecondParticipant: ComputedRef<Participant | undefined>
  }

  getTeamSideParticipant(teamSide: TeamSide): Participant | undefined

  getIncidentTeamSide(
    incident: Partial<Pick<Incident, 'participant_team_id'>>,
  ): TeamSide | null
}

export function useTeamSides(): UseTeamSides {
  const { eventSettings, updateEventSettings } = useEventSettings()
  const { homeParticipant, awayParticipant } = useParticipants()

  const currentFirstTeamSide = computed(() =>
    eventSettings.value.teamSideChanged ? 'away' : 'home',
  )
  const currentSecondTeamSide = computed(() =>
    eventSettings.value.teamSideChanged ? 'home' : 'away',
  )

  function changeSides(): void {
    updateEventSettings({
      teamSideChanged: !eventSettings.value.teamSideChanged,
    })
  }

  function getWithCurrentTeamSides<T>(
    homeValue: Ref<T>,
    awayValue: Ref<T>,
  ): [ComputedRef<T>, ComputedRef<T>] {
    return [
      computed(() =>
        currentFirstTeamSide.value === 'home'
          ? homeValue.value
          : awayValue.value,
      ),
      computed(() =>
        currentSecondTeamSide.value === 'away'
          ? awayValue.value
          : homeValue.value,
      ),
    ]
  }

  function getParticipantsWithCurrentTeamSides(): {
    currentFirstParticipant: ComputedRef<Participant | undefined>
    currentSecondParticipant: ComputedRef<Participant | undefined>
  } {
    const [currentFirstParticipant, currentSecondParticipant] =
      getWithCurrentTeamSides(homeParticipant, awayParticipant)

    return {
      currentFirstParticipant,
      currentSecondParticipant,
    }
  }

  function getIncidentTeamSide(
    incident: Partial<Pick<Incident, 'participant_team_id'>>,
  ): TeamSide | null {
    if (!homeParticipant.value || !awayParticipant.value) {
      return null
    }

    return getParticipantIdTeamSide(
      incident.participant_team_id,
      homeParticipant.value,
      awayParticipant.value,
    )
  }

  function getTeamSideParticipant(teamSide: TeamSide): Participant | undefined {
    return teamSide === 'home' ? homeParticipant.value : awayParticipant.value
  }

  return {
    changeSides,
    currentFirstTeamSide,
    currentSecondTeamSide,
    getIncidentTeamSide,
    getTeamSideParticipant,
    getWithCurrentTeamSides,
    getParticipantsWithCurrentTeamSides,
  }
}
